import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    ButtonBase,
    Paper,
    Stack,
    Button,
    CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { allSeatsAPI } from "../api/trips";

import { useSnackbar } from "notistack";
import i18n from "../locales/i18n";

export default function SeatSelection({ trip, journeyDate, passengers, selectedSeats, onSeatsChange, refreshKey = 0 }) {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [seats, setSeats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSeatPicker, setShowSeatPicker] = useState(false);
    const [fetched, setFetched] = useState(false);

    const isNumbered = trip?.seatsNumbered === true;

    const fetchSeats = async () => {
        const tripId = trip?.id || trip?.tripId;
        if (!tripId) return;

        setLoading(true);
        try {
            const data = await allSeatsAPI({
                tripId,
                date: journeyDate,
                depId: trip.departureStationId || trip.fromStationId || null,
                arrId: trip.arrivalStationId || trip.toStationId || null,
            });
            const fresh = data || [];
            setSeats(fresh);
            // Sedišta iz izbora koja su u međuvremenu zauzeta ispadaju iz izbora —
            // inače bi korisnik ponovo poslao isto (sad tuđe) sedište.
            const stillFree = selectedSeats.filter((s) => {
                const match = fresh.find((f) => getSeatNum(f) === getSeatNum(s));
                return match ? !isSeatUnavailable(match) : false;
            });
            if (stillFree.length !== selectedSeats.length) onSeatsChange(stillFree);
        } catch (error) {
            console.error("Greška pri učitavanju sedišta:", error);
            enqueueSnackbar(i18n.t("common.errorLoadingSeats"), { variant: "error" });
            setSeats([]);
        } finally {
            setLoading(false);
            setFetched(true);
        }
    };

    // Roditelj podigne refreshKey kad rezervacija padne na „sedište zauzeto" —
    // mapa se ponovo učita da korisnik ne bira po bajatom snimku.
    useEffect(() => {
        if (refreshKey > 0) {
            fetchSeats();
            setShowSeatPicker(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    const handleOpenSeatPicker = () => {
        if (!fetched) fetchSeats();
        setShowSeatPicker(true);
    };

    const getSeatNum = (seat) => seat.seatNumber || seat.seat || seat.number;

    const isSeatUnavailable = (seat) =>
        seat.occupied === true ||
        seat.reserved === true ||
        (typeof seat.reservationExpireSec === "number" && seat.reservationExpireSec > 180) ||
        seat.selectableByCurrentOperator === false;

    const handleSeatClick = (seat) => {
        if (isSeatUnavailable(seat)) return;

        const seatNum = getSeatNum(seat);
        const isSelected = selectedSeats.some((s) => getSeatNum(s) === seatNum);
        if (isSelected) {
            onSeatsChange(selectedSeats.filter((s) => getSeatNum(s) !== seatNum));
        } else if (selectedSeats.length < passengers) {
            onSeatsChange([...selectedSeats, { ...seat, seatNumber: seatNum }]);
        }
    };

    const getSeatState = (seat) => {
        const seatNum = getSeatNum(seat);
        if (selectedSeats.some((s) => getSeatNum(s) === seatNum)) return "selected";
        if (isSeatUnavailable(seat)) return "occupied";
        return "free";
    };

    // Alotman nije numerisan - ne prikazujemo biranje sedista
    if (!isNumbered) {
        return (
            <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid", borderColor: "grey.200" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Icon icon="mdi:seat-outline" width={22} color="#919EAB" />
                    <Typography variant="body2" color="text.secondary">
                        {t("booking.seatAutoAssigned")}
                    </Typography>
                </Stack>
            </Paper>
        );
    }

    // Numerisan alotman ali korisnik jos nije kliknuo da bira
    if (!showSeatPicker) {
        return (
            <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid", borderColor: "grey.200" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Icon icon="mdi:seat-recline-normal" width={22} color="#1a1a2e" />
                        <Stack>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {t("booking.numberedSeatsTitle")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t("booking.numberedSeatsNote")}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenSeatPicker}
                        startIcon={<Icon icon="mdi:seat" width={18} />}
                        sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none", whiteSpace: "nowrap" }}
                    >
                        {t("booking.selectSeat")}
                    </Button>
                </Stack>
            </Paper>
        );
    }

    // Sedišta grupisana u redove po 4 (2 + prolaz + 2)
    const seatRows = [];
    for (let i = 0; i < seats.length; i += 4) {
        seatRows.push(seats.slice(i, i + 4));
    }

    // Prikaz sedišta
    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("booking.selectSeatCount", { selected: selectedSeats.length, total: passengers })}
                </Typography>
                <Button
                    size="small"
                    onClick={() => { setShowSeatPicker(false); onSeatsChange([]); }}
                    sx={{ fontWeight: 600, textTransform: "none", fontSize: "0.8rem" }}
                >
                    {t("booking.cancelSelection")}
                </Button>
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : seats.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("booking.seatsUnavailable")}
                </Typography>
            ) : (
                <>
                    {/* Legenda */}
                    <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
                        <LegendItem state="free" label={t("booking.seatFree")} />
                        <LegendItem state="selected" label={t("booking.seatSelected")} />
                        <LegendItem state="occupied" label={t("booking.seatOccupied")} />
                    </Stack>

                    {/* Šema autobusa */}
                    <Box
                        sx={{
                            maxWidth: 280,
                            mx: "auto",
                            p: 2,
                            pt: 1.5,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "grey.300",
                            bgcolor: "grey.100",
                        }}
                    >
                        {/* Prednji deo autobusa - volan */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={0.75}
                            sx={{
                                pb: 1.5,
                                mb: 1.5,
                                borderBottom: "1px dashed",
                                borderColor: "grey.300",
                                color: "text.secondary",
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
                                {t("booking.seatBusFront")}
                            </Typography>
                            <Icon icon="mdi:steering" width={20} />
                        </Stack>

                        {/* Redovi sedišta */}
                        <Stack spacing={1.25}>
                            {seatRows.map((row, rowIndex) => (
                                <Box
                                    key={rowIndex}
                                    sx={{ display: "flex", justifyContent: "center", gap: 1, alignItems: "flex-end" }}
                                >
                                    {row.map((seat, colIndex) => {
                                        const seatNum = getSeatNum(seat) || rowIndex * 4 + colIndex + 1;
                                        return (
                                            <Box key={seatNum} sx={{ ...(colIndex === 2 && { ml: 2 }) }}>
                                                <SeatButton
                                                    num={seatNum}
                                                    state={getSeatState(seat)}
                                                    onClick={() => handleSeatClick(seat)}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </>
            )}
        </Paper>
    );
}

const SEAT_PALETTE = {
    free: { bg: "background.paper", border: "success.main", text: "success.dark", hoverBg: "success.lighter" },
    selected: { bg: "secondary.main", border: "secondary.main", text: "common.white", hoverBg: "secondary.dark" },
    occupied: { bg: "grey.200", border: "grey.300", text: "grey.400", hoverBg: "grey.200" },
};

function SeatButton({ num, state, onClick }) {
    const isOccupied = state === "occupied";
    const isSelected = state === "selected";
    const palette = SEAT_PALETTE[state];

    return (
        <ButtonBase
            onClick={onClick}
            disabled={isOccupied}
            disableRipple={isOccupied}
            aria-label={`Sedište ${num}`}
            sx={{
                position: "relative",
                width: 44,
                height: 44,
                borderRadius: "6px 6px 10px 10px",
                border: "2px solid",
                borderColor: palette.border,
                bgcolor: palette.bg,
                color: palette.text,
                cursor: isOccupied ? "not-allowed" : "pointer",
                transition: "transform .15s ease, box-shadow .15s ease, background-color .15s ease",
                boxShadow: isSelected ? "0 4px 12px rgba(51,102,255,0.35)" : "none",
                // naslon za glavu
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -4,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 24,
                    height: 7,
                    borderRadius: 4,
                    backgroundColor: palette.border,
                },
                "&:hover": isOccupied
                    ? {}
                    : {
                          backgroundColor: palette.hoverBg,
                          transform: "translateY(-2px)",
                          boxShadow: isSelected
                              ? "0 6px 14px rgba(51,102,255,0.4)"
                              : "0 4px 10px rgba(0,0,0,0.12)",
                      },
            }}
        >
            {isSelected ? (
                <Icon icon="mdi:check-bold" width={20} />
            ) : (
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.8rem", lineHeight: 1 }}>
                    {num}
                </Typography>
            )}
        </ButtonBase>
    );
}

function LegendItem({ state, label }) {
    const palette = SEAT_PALETTE[state];
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "3px 3px 5px 5px",
                    border: "2px solid",
                    borderColor: palette.border,
                    bgcolor: palette.bg,
                }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {label}
            </Typography>
        </Stack>
    );
}
